import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../services/authService';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { Plus, Edit, Trash2, Save, X } from '@ui/icons';
import { socketService } from '@platform/socket';

interface KomunikatItem {
  id: number;
  region_id: number;
  tresc: string | null;
  dzien: string;
  region_nazwa?: string;
  uzytkownik_login?: string;
}

interface KomunikatyWidgetProps {
  selectedRegion: number | null;
  selectedDate: string;
  hideHeader?: boolean;
}

const KomunikatyWidget: React.FC<KomunikatyWidgetProps> = ({ selectedRegion, selectedDate, hideHeader = false }) => {
  const { user } = useAuth();
  const [komunikaty, setKomunikaty] = useState<KomunikatItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const selectedDateRef = useRef<string>(selectedDate);
  const selectedRegionRef = useRef<number | null>(selectedRegion);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    selectedRegionRef.current = selectedRegion;
  }, [selectedRegion]);

  // Focus add input when adding
  useEffect(() => {
    if (isAdding) {
      addInputRef.current?.focus();
    }
  }, [isAdding]);

  // Load komunikaty
  const loadKomunikaty = useCallback(async () => {
    try {
      if (!selectedRegionRef.current) {
        setKomunikaty([]);
        return;
      }

      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `/api/komunikat?date=${selectedDateRef.current}&region_id=${selectedRegionRef.current}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        const list: KomunikatItem[] = (data.komunikaty || []) as KomunikatItem[];
        list.sort((a, b) => (b.id || 0) - (a.id || 0));
        setKomunikaty(list);
      } else {
        setError('Błąd pobierania komunikatów');
      }
    } catch (e: any) {
      setError(e?.message || 'Błąd pobierania komunikatów');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount and when date/region changes (only after authentication)
  useAuthenticatedEffect(() => {
    if (selectedRegion !== null) {
      loadKomunikaty();
    }
  }, [selectedDate, selectedRegion, loadKomunikaty]);

  // Initialize socket and listen for realtime updates
  useEffect(() => {
    let socketUnsubscribe: (() => void) | null = null;

    const initSocket = async () => {
      try {
        await socketService.init();
        socketUnsubscribe = socketService.subscribe((evt: any) => {
          try {
            if (!evt || evt.table !== 'komunikat') return;

            const op = String(evt.operation || '').toUpperCase();

            if (op === 'DELETE') {
              const delId = Number((evt.id ?? evt.pk?.id ?? evt.row?.id ?? evt.old_row?.id) || 0);
              if (delId) {
                setKomunikaty(prev => prev.filter(k => Number(k.id) !== delId));
              } else {
                loadKomunikaty();
              }
              return;
            }

            const row = evt.row || {};
            const sameRegion =
              selectedRegionRef.current !== null
                ? Number((row.region_id ?? evt.region_id) || 0) === Number(selectedRegionRef.current)
                : true;
            const rawDate = row.dzien ?? evt.dzien;
            const sameDate = rawDate
              ? String(rawDate).slice(0, 10) === String(selectedDateRef.current).slice(0, 10)
              : true;

            if (sameRegion && sameDate) {
              loadKomunikaty();
            }
          } catch (err) {
            console.error('[KomunikatyWidget] error in socket handler:', err);
          }
        });
      } catch (err) {
        console.error('[KomunikatyWidget] error initializing socket:', err);
      }
    };

    initSocket();

    // Also listen to window CustomEvent
    const customEventHandler = (evt: CustomEvent) => {
      try {
        const detail = evt.detail;
        if (!detail || detail.table !== 'komunikat') return;

        const op = String(detail.operation || '').toUpperCase();

        if (op === 'DELETE') {
          const delId = Number((detail.id ?? detail.pk?.id ?? detail.row?.id ?? detail.old_row?.id) || 0);
          if (delId) {
            setKomunikaty(prev => prev.filter(k => Number(k.id) !== delId));
          } else {
            loadKomunikaty();
          }
          return;
        }

        const row = detail.row || {};
        const sameRegion =
          selectedRegionRef.current !== null
            ? Number((row.region_id ?? detail.region_id) || 0) === Number(selectedRegionRef.current)
            : true;
        const rawDate = row.dzien ?? detail.dzien;
        const sameDate = rawDate
          ? String(rawDate).slice(0, 10) === String(selectedDateRef.current).slice(0, 10)
          : true;

        if (sameRegion && sameDate) {
          loadKomunikaty();
        }
      } catch (err) {
        console.error('[KomunikatyWidget] error in CustomEvent handler:', err);
      }
    };

    window.addEventListener('komunikat:change', customEventHandler as EventListener);
    window.addEventListener('db:change', customEventHandler as EventListener);

    return () => {
      if (socketUnsubscribe) {
        socketUnsubscribe();
      }
      window.removeEventListener('komunikat:change', customEventHandler as EventListener);
      window.removeEventListener('db:change', customEventHandler as EventListener);
    };
  }, [loadKomunikaty]);

  // Add new komunikat
  const handleAdd = async () => {
    try {
      if (!selectedRegion || !newText.trim()) return;

      const response = await authenticatedFetch('/api/komunikat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region_id: selectedRegion,
          tresc: newText.trim(),
          dzien: selectedDate,
          uzytkownik_login: user?.login || ''
        })
      });

      if (response.ok) {
        setNewText('');
        setIsAdding(false);
        await loadKomunikaty();
      } else {
        setError('Błąd dodawania komunikatu');
      }
    } catch (e: any) {
      setError(e?.message || 'Błąd dodawania komunikatu');
    }
  };

  // Update komunikat
  const handleUpdate = async (id: number) => {
    try {
      if (!selectedRegion || !editText.trim()) return;

      const response = await authenticatedFetch(`/api/komunikat/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region_id: selectedRegion,
          tresc: editText.trim(),
          dzien: selectedDate,
          uzytkownik_login: user?.login || ''
        })
      });

      if (response.ok) {
        setIsEditing(null);
        setEditText('');
        await loadKomunikaty();
      } else {
        setError('Błąd aktualizacji komunikatu');
      }
    } catch (e: any) {
      setError(e?.message || 'Błąd aktualizacji komunikatu');
    }
  };

  // Delete komunikat
  const handleDelete = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten komunikat?')) return;

    try {
      const response = await authenticatedFetch(`/api/komunikat/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await loadKomunikaty();
      } else {
        setError('Błąd usuwania komunikatu');
      }
    } catch (e: any) {
      setError(e?.message || 'Błąd usuwania komunikatu');
    }
  };

  const startEdit = (komunikat: KomunikatItem) => {
    setIsEditing(komunikat.id);
    setEditText(komunikat.tresc || '');
  };

  const cancelEdit = () => {
    setIsEditing(null);
    setEditText('');
  };

  const cancelAdd = () => {
    setIsAdding(false);
    setNewText('');
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        border: hideHeader ? 'none' : '1px solid var(--border)',
        borderRadius: hideHeader ? '0' : '4px',
        background: 'var(--card)',
        minWidth: 0,
        overflow: 'hidden',
        minHeight: hideHeader ? '160px' : 'auto',
        height: hideHeader ? '160px' : 'auto'
      }}
    >
      {!hideHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 10px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg)'
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Komunikat</span>
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            {komunikaty.length > 0 ? `${komunikaty.length} komunikatów` : 'Brak komunikatów'}
          </span>
        </div>
      )}

      <div
        style={{
          padding: hideHeader ? '0' : '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          flex: 1,
          minHeight: 0,
          height: hideHeader ? '100%' : 'auto'
        }}
      >
        {loading && <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Ładowanie...</div>}
        {!loading && error && (
          <div style={{ fontSize: '11px', color: 'var(--error)' }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Add Button */}
            {!isAdding && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '4px' }}>
                <button
                  onClick={() => setIsAdding(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    color: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px'
                  }}
                  title="Dodaj komunikat"
                >
                  <Plus style={{ width: '14px', height: '14px' }} />
                  <span>Dodaj</span>
                </button>
              </div>
            )}

            {/* Add Input */}
            {isAdding && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  padding: '8px',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  alignItems: 'center',
                  background: 'var(--bg)'
                }}
              >
                <input
                  type="text"
                  ref={addInputRef}
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newText.trim()) {
                      handleAdd();
                    } else if (e.key === 'Escape') {
                      cancelAdd();
                    }
                  }}
                  placeholder="Wpisz treść komunikatu..."
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: '11px',
                    border: '1px solid var(--border)',
                    borderRadius: '3px',
                    background: 'var(--card)',
                    color: 'var(--text)'
                  }}
                />
                <button
                  onClick={handleAdd}
                  disabled={!newText.trim()}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: newText.trim() ? 'pointer' : 'not-allowed',
                    padding: '4px',
                    color: newText.trim() ? 'var(--ok)' : 'var(--muted)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Zapisz"
                >
                  <Save style={{ width: '14px', height: '14px' }} />
                </button>
                <button
                  onClick={cancelAdd}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'var(--error)',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Anuluj"
                >
                  <X style={{ width: '14px', height: '14px' }} />
                </button>
              </div>
            )}

            {/* Messages List */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                flex: 1,
                overflowY: 'auto',
                maxHeight: hideHeader ? '120px' : '400px',
                minHeight: hideHeader ? '120px' : '200px'
              }}
            >
              {komunikaty.length === 0 && !loading && (
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--muted)',
                    padding: '12px',
                    textAlign: 'center'
                  }}
                >
                  Brak komunikatów
                </div>
              )}

              {komunikaty.map((komunikat, index) => (
                <div
                  key={komunikat.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    background: 'var(--card)',
                    borderBottom: index < komunikaty.length - 1 ? '1px solid var(--border)' : 'none',
                    minHeight: '32px'
                  }}
                >
                  {isEditing === komunikat.id ? (
                    <>
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && editText.trim()) {
                            handleUpdate(komunikat.id);
                          } else if (e.key === 'Escape') {
                            cancelEdit();
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '4px 8px',
                          fontSize: '11px',
                          border: '1px solid var(--border)',
                          borderRadius: '3px',
                          background: 'var(--card)',
                          color: 'var(--text)'
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => handleUpdate(komunikat.id)}
                        disabled={!editText.trim()}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: editText.trim() ? 'pointer' : 'not-allowed',
                          padding: '4px',
                          color: editText.trim() ? 'var(--ok)' : 'var(--muted)',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Zapisz"
                      >
                        <Save style={{ width: '14px', height: '14px' }} />
                      </button>
                      <button
                        onClick={cancelEdit}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--error)',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Anuluj"
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                      </button>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          flex: 1,
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: 'var(--text)',
                          wordBreak: 'break-word'
                        }}
                        onDoubleClick={() => startEdit(komunikat)}
                      >
                        {komunikat.tresc}
                      </div>
                      {komunikat.uzytkownik_login && (
                        <span
                          style={{
                            fontSize: '9px',
                            color: 'var(--muted)',
                            fontStyle: 'italic',
                            marginRight: '4px'
                          }}
                        >
                          {komunikat.uzytkownik_login}
                        </span>
                      )}
                      <button
                        onClick={() => startEdit(komunikat)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--accent)',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Edytuj"
                      >
                        <Edit style={{ width: '14px', height: '14px' }} />
                      </button>
                      <button
                        onClick={() => handleDelete(komunikat.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--error)',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Usuń"
                      >
                        <Trash2 style={{ width: '14px', height: '14px' }} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KomunikatyWidget;

